import * as d3 from 'd3';
import type {
  ChartConfig,
  ChartDataModel,
  Datum,
  RenderContext,
  Series
} from '@chartforge/shared-types';
import { bool, num, str } from './config-access';
import { formatValue, type TimeScaleMode } from './format';
import { paletteFor } from './theme';

const CATEGORY_AXIS_WIDTH = 110;
const X_AXIS_HEIGHT = 34;

const TICK_FORMATS: Record<string, string> = {
  year: '%Y',
  month: '%b %Y',
  week: '%b %d',
  day: '%b %d'
};

const MARKER_SYMBOLS: Record<string, d3.SymbolType> = {
  diamond: d3.symbolDiamond,
  square: d3.symbolSquare
};

/**
 * Resolve the effective time-scale mode. 'auto' uses the data model:
 * `meta.units === 'date'` or epoch-millisecond-sized values mean time.
 */
export function resolveTimeScale(option: string, model: ChartDataModel): TimeScaleMode {
  if (option === 'time' || option === 'numeric') {
    return option;
  }
  if (model.meta?.units === 'date') {
    return 'time';
  }
  const maxValue = d3.max(model.series.flatMap((s) => s.points.map((p) => p.value)));
  return maxValue !== undefined && maxValue > 1e11 ? 'time' : 'numeric';
}

function orderCategories(model: ChartDataModel, sortMode: string): string[] {
  if (sortMode !== 'ascending' && sortMode !== 'descending') {
    return model.categories;
  }
  const minValue = new Map<string, number>();
  for (const series of model.series) {
    for (const point of series.points) {
      const current = minValue.get(point.category);
      if (current === undefined || point.value < current) {
        minValue.set(point.category, point.value);
      }
    }
  }
  const sorted = [...model.categories].sort(
    (a, b) => (minValue.get(a) ?? 0) - (minValue.get(b) ?? 0)
  );
  return sortMode === 'descending' ? sorted.reverse() : sorted;
}

/**
 * Render the timeline chart into ctx.container as a single SVG.
 * Pure D3 — no framework, no host APIs.
 */
export function renderTimeline(
  model: ChartDataModel,
  config: ChartConfig,
  ctx: RenderContext
): void {
  const { container, width } = ctx;
  container.replaceChildren();
  if (!container.style.position) {
    container.style.position = 'relative';
  }

  /* ---- configuration (all schema-driven) ---- */
  const timeMode = resolveTimeScale(str(config, 'data', 'timeline.timeScale', 'auto'), model);
  const granularity = str(config, 'data', 'timeline.timeGranularity', 'auto');
  const valueFormat = str(config, 'data', 'data.valueFormat', 'default');
  const rowHeight = num(config, 'layout', 'timeline.rowHeight', 48);
  const margin = num(config, 'layout', 'spacing.margin', 16);
  const padding = num(config, 'layout', 'spacing.padding', 8);
  const showLegend = bool(config, 'layout', 'layout.showLegend', true);

  const background = str(config, 'appearance', 'appearance.background', '#ffffff');
  const borderColor = str(config, 'appearance', 'appearance.border', '#e5e7eb');
  const borderWidth = num(config, 'appearance', 'appearance.borderWidth', 1);
  const borderRadius = num(config, 'appearance', 'appearance.borderRadius', 8);
  const palette = paletteFor(str(config, 'appearance', 'appearance.colorScheme', 'default'));
  const fontFamily = str(config, 'appearance', 'font.family', 'Inter, system-ui, sans-serif');
  const fontSize = num(config, 'appearance', 'font.size', 12);
  const fontWeight = str(config, 'appearance', 'font.weight', '400');
  const markerShape = str(config, 'appearance', 'timeline.markerShape', 'circle');
  const markerSize = num(config, 'appearance', 'timeline.markerSize', 12);
  const connectPoints = bool(config, 'appearance', 'timeline.connectPoints', true);

  const showTitle = bool(config, 'labels', 'labels.showTitle', false);
  const title = str(config, 'labels', 'labels.title', '');
  const titleSize = num(config, 'labels', 'labels.titleSize', 16);
  const showValueLabels = bool(config, 'labels', 'labels.showValues', false);
  const valueLabelSize = num(config, 'labels', 'labels.size', 11);
  const valueLabelColor = str(config, 'labels', 'labels.color', '#374151');
  const tooltipEnabled = bool(config, 'labels', 'timeline.tooltip', true);

  const xAxisVisible = bool(config, 'axes', 'axes.x.visible', true);
  const yAxisVisible = bool(config, 'axes', 'axes.y.visible', true);
  const xAxisTitle = str(config, 'axes', 'axes.x.title', '');
  const yAxisTitle = str(config, 'axes', 'axes.y.title', '');
  const gridlines = bool(config, 'axes', 'axes.gridlines', true);
  const gridColor = str(config, 'axes', 'axes.gridColor', '#e5e7eb');
  const axisColor = str(config, 'axes', 'axes.axisColor', '#6b7280');

  const categories = orderCategories(model, str(config, 'data', 'data.sortCategories', 'none'));

  /* ---- geometry ---- */
  const titleHeight = showTitle && title ? titleSize + 14 : 0;
  const legendHeight = showLegend && model.series.length > 0 ? 26 : 0;
  const m = {
    top: margin + padding + titleHeight + 10,
    right: margin + padding + 20,
    bottom: margin + padding + X_AXIS_HEIGHT + legendHeight,
    left: margin + padding + CATEGORY_AXIS_WIDTH
  };
  const innerWidth = Math.max(60, width - m.left - m.right);
  const innerHeight = Math.max(categories.length * rowHeight, ctx.height - m.top - m.bottom, 80);
  const svgHeight = innerHeight + m.top + m.bottom;

  /* ---- scales ---- */
  const values = model.series.flatMap((s) => s.points.map((p) => p.value));
  const y = d3.scaleBand<string>().domain(categories).range([0, innerHeight]).padding(0.25);

  const xNumeric = d3
    .scaleLinear()
    .domain(values.length ? [d3.min(values)!, d3.max(values)!] : [0, 1])
    .range([0, innerWidth])
    .nice();
  const xTime = d3
    .scaleTime()
    .domain(
      values.length
        ? [new Date(d3.min(values)!), new Date(d3.max(values)!)]
        : [new Date(0), new Date(1)]
    )
    .range([0, innerWidth])
    .nice();

  const xPos = (v: number): number => (timeMode === 'time' ? xTime(new Date(v)) : xNumeric(v));
  const rowY = (category: string): number => (y(category) ?? 0) + y.bandwidth() / 2;

  /* ---- svg shell + theming ---- */
  const svg = d3
    .select(container)
    .append('svg')
    .attr('class', 'cf-chart cf-timeline')
    .attr('width', width)
    .attr('height', svgHeight)
    .attr('role', 'img')
    .attr('aria-label', 'Timeline chart')
    .style('display', 'block')
    .style('font-family', fontFamily)
    .style('font-size', `${fontSize}px`)
    .style('font-weight', fontWeight);

  const inset = borderWidth / 2;
  svg
    .append('rect')
    .attr('class', 'cf-background')
    .attr('x', inset)
    .attr('y', inset)
    .attr('width', Math.max(0, width - borderWidth))
    .attr('height', Math.max(0, svgHeight - borderWidth))
    .attr('rx', borderRadius)
    .attr('fill', background)
    .attr('stroke', borderColor)
    .attr('stroke-width', borderWidth);

  if (showTitle && title) {
    svg
      .append('text')
      .attr('class', 'cf-title')
      .attr('x', m.left)
      .attr('y', margin + padding + titleSize)
      .attr('font-size', titleSize)
      .attr('font-weight', 600)
      .attr('fill', '#111827')
      .text(title);
  }

  const plot = svg
    .append('g')
    .attr('class', 'cf-plot')
    .attr('transform', `translate(${m.left},${m.top})`);

  /* ---- gridlines ---- */
  const tickValues =
    timeMode === 'time' ? (xTime.ticks(6).map((t) => t.getTime()) as number[]) : xNumeric.ticks(6);

  if (gridlines) {
    const grid = plot.append('g').attr('class', 'cf-gridlines');
    grid
      .selectAll('line')
      .data(tickValues)
      .join('line')
      .attr('x1', (t) => xPos(t))
      .attr('x2', (t) => xPos(t))
      .attr('y1', 0)
      .attr('y2', innerHeight)
      .attr('stroke', gridColor)
      .attr('stroke-width', 1)
      .attr('shape-rendering', 'crispEdges');
  }

  /* ---- axes ---- */
  const styleAxis = (g: d3.Selection<SVGGElement, unknown, null, undefined>): void => {
    g.selectAll('text').attr('fill', axisColor);
    g.selectAll('line').attr('stroke', axisColor);
    g.selectAll('path').attr('stroke', axisColor);
  };

  const xAxisG = plot
    .append('g')
    .attr('class', 'cf-axis cf-axis-x')
    .attr('transform', `translate(0,${innerHeight})`);
  if (xAxisVisible) {
    const axisScale = (timeMode === 'time' ? xTime : xNumeric) as unknown as d3.AxisScale<
      string | number | Date
    >;
    const generator = d3.axisBottom(axisScale).ticks(6);
    if (timeMode === 'time' && granularity !== 'auto' && TICK_FORMATS[granularity]) {
      const fmt = d3.timeFormat(TICK_FORMATS[granularity]!);
      generator.tickFormat((d) => fmt(d as Date));
    }
    xAxisG.call(generator);
    styleAxis(xAxisG);
    if (xAxisTitle) {
      xAxisG
        .append('text')
        .attr('class', 'cf-axis-title')
        .attr('x', innerWidth / 2)
        .attr('y', 32)
        .attr('text-anchor', 'middle')
        .attr('fill', axisColor)
        .text(xAxisTitle);
    }
  } else {
    xAxisG.remove();
  }

  const yAxisG = plot.append('g').attr('class', 'cf-axis cf-axis-y');
  if (yAxisVisible) {
    yAxisG.call(d3.axisLeft(y));
    styleAxis(yAxisG);
    yAxisG.select('path').remove();
    if (yAxisTitle) {
      yAxisG
        .append('text')
        .attr('class', 'cf-axis-title')
        .attr('transform', `translate(${-CATEGORY_AXIS_WIDTH + 14},${innerHeight / 2}) rotate(-90)`)
        .attr('text-anchor', 'middle')
        .attr('fill', axisColor)
        .text(yAxisTitle);
    }
  } else {
    yAxisG.remove();
  }

  /* ---- tooltip ---- */
  const tooltip = tooltipEnabled ? createTooltip(container) : null;

  /* ---- series: connecting lines + milestone markers ---- */
  model.series.forEach((series, seriesIndex) => {
    const color = palette[seriesIndex % palette.length]!;
    const g = plot.append('g').attr('class', 'cf-series').attr('data-series-id', series.id);
    const points = [...series.points].sort((a, b) => a.value - b.value);

    if (connectPoints && points.length > 1) {
      const pathData = d3
        .line<Datum>()
        .x((d) => xPos(d.value))
        .y((d) => rowY(d.category))(points);
      g.append('path')
        .attr('class', 'cf-line')
        .attr('fill', 'none')
        .attr('stroke', color)
        .attr('stroke-width', 2)
        .attr('stroke-opacity', 0.55)
        .attr('d', pathData);
    }

    for (const datum of points) {
      const cx = xPos(datum.value);
      const cy = rowY(datum.category);
      const marker = appendMarker(g, markerShape, markerSize, cx, cy, color);
      marker
        .attr('class', 'cf-marker')
        .attr('data-series', series.id)
        .attr('data-category', datum.category)
        .attr('data-value', datum.value);

      if (showValueLabels) {
        g.append('text')
          .attr('class', 'cf-value-label')
          .attr('x', cx + markerSize / 2 + 6)
          .attr('y', cy)
          .attr('dy', '0.32em')
          .attr('font-size', valueLabelSize)
          .attr('fill', valueLabelColor)
          .text(formatValue(datum.value, timeMode, valueFormat));
      }

      if (tooltip) {
        attachTooltipEvents(marker.node()!, tooltip, container, series, datum, () =>
          formatValue(datum.value, timeMode, valueFormat)
        );
      }
    }
  });

  /* ---- legend ---- */
  if (showLegend && model.series.length > 0) {
    const legend = svg
      .append('g')
      .attr('class', 'cf-legend')
      .attr('transform', `translate(${m.left},${svgHeight - margin - padding - 14})`);
    let cursor = 0;
    model.series.forEach((series, i) => {
      const item = legend.append('g').attr('transform', `translate(${cursor},0)`);
      item
        .append('rect')
        .attr('width', 10)
        .attr('height', 10)
        .attr('y', -8)
        .attr('rx', 2)
        .attr('fill', palette[i % palette.length]!);
      item.append('text').attr('x', 15).attr('fill', '#374151').text(series.name);
      cursor += 15 + series.name.length * fontSize * 0.62 + 20;
    });
  }
}

/* ------------------------------------------------------------------ */
/* internals                                                           */
/* ------------------------------------------------------------------ */

function appendMarker(
  g: d3.Selection<SVGGElement, unknown, null, undefined>,
  shape: string,
  size: number,
  cx: number,
  cy: number,
  color: string
): d3.Selection<SVGCircleElement | SVGPathElement, unknown, null, undefined> {
  const symbolType = MARKER_SYMBOLS[shape];
  if (symbolType) {
    return g
      .append('path')
      .attr(
        'd',
        d3
          .symbol()
          .type(symbolType)
          .size(size * size * 1.4)() ?? ''
      )
      .attr('transform', `translate(${cx},${cy})`)
      .attr('fill', color)
      .attr('stroke', '#ffffff')
      .attr('stroke-width', 1.5) as d3.Selection<
      SVGCircleElement | SVGPathElement,
      unknown,
      null,
      undefined
    >;
  }
  return g
    .append('circle')
    .attr('cx', cx)
    .attr('cy', cy)
    .attr('r', size / 2)
    .attr('fill', color)
    .attr('stroke', '#ffffff')
    .attr('stroke-width', 1.5) as d3.Selection<
    SVGCircleElement | SVGPathElement,
    unknown,
    null,
    undefined
  >;
}

function createTooltip(container: HTMLElement): HTMLDivElement {
  const tooltip = document.createElement('div');
  tooltip.className = 'cf-tooltip';
  Object.assign(tooltip.style, {
    position: 'absolute',
    display: 'none',
    pointerEvents: 'none',
    background: 'rgba(17, 24, 39, 0.92)',
    color: '#f9fafb',
    padding: '6px 10px',
    borderRadius: '6px',
    fontSize: '12px',
    lineHeight: '1.4',
    whiteSpace: 'nowrap',
    zIndex: '10'
  } satisfies Partial<CSSStyleDeclaration>);
  container.appendChild(tooltip);
  return tooltip;
}

function attachTooltipEvents(
  node: SVGElement,
  tooltip: HTMLDivElement,
  container: HTMLElement,
  series: Series,
  datum: Datum,
  formattedValue: () => string
): void {
  node.addEventListener('mouseenter', () => {
    tooltip.replaceChildren();
    const title = document.createElement('strong');
    title.textContent = series.name;
    tooltip.appendChild(title);
    tooltip.appendChild(document.createElement('br'));
    tooltip.appendChild(document.createTextNode(`${datum.category}: ${formattedValue()}`));
    tooltip.style.display = 'block';
  });
  node.addEventListener('mousemove', (event) => {
    const rect = container.getBoundingClientRect();
    const e = event as MouseEvent;
    tooltip.style.left = `${e.clientX - rect.left + 12}px`;
    tooltip.style.top = `${e.clientY - rect.top + 12}px`;
  });
  node.addEventListener('mouseleave', () => {
    tooltip.style.display = 'none';
  });
}
